import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import api from "../api";
import "./AcceptInvite.css";

const ROLE_INFO = {
  loan_officer: { icon: "🏦", label: "Loan Officer", color: "green" },
  manager:      { icon: "👔", label: "Manager",      color: "blue"  },
  admin:        { icon: "⚙️", label: "Admin",        color: "purple"},
  executive:    { icon: "📊", label: "Executive",    color: "yellow"},
};

const steps = ["Welcome", "Your Info", "Set Password", "Done"];

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate  = useNavigate();
  const { login } = useAuth();

  const [preview, setPreview]     = useState(null);
  const [previewErr, setPreviewErr] = useState(null);
  const [step, setStep]           = useState(0);

  // Form fields
  const [fullName, setFullName]   = useState("");
  const [email, setEmail]         = useState("");
  const [nmls, setNmls]           = useState("");
  const [password, setPassword]   = useState("");
  const [confirm, setConfirm]     = useState("");
  const [pwStrength, setPwStrength] = useState(0);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState("");
  const [success, setSuccess]       = useState(false);

  useEffect(() => {
    api.get(`/invites/preview/${token}`)
      .then(r => {
        setPreview(r.data);
        if (r.data.email) setEmail(r.data.email);
      })
      .catch(() => setPreviewErr("This invite link is invalid or has expired."));
  }, [token]);

  const checkStrength = (pw) => {
    let score = 0;
    if (pw.length >= 8)  score++;
    if (pw.length >= 12) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    setPwStrength(score);
  };

  const strengthLabel = ["", "Weak", "Fair", "Good", "Strong", "Very Strong"][pwStrength];
  const strengthColor = ["", "#ef4444", "#f59e0b", "#f59e0b", "#10b981", "#10b981"][pwStrength];

  const handleSubmit = async () => {
    setError("");
    if (!fullName.trim()) { setError("Please enter your full name."); return; }
    if (!email.trim())    { setError("Please enter your email address."); return; }
    if (preview?.role === "loan_officer" && !nmls.trim()) { setError("NMLS number is required."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (password.length < 8)  { setError("Password must be at least 8 characters."); return; }

    setSubmitting(true);
    try {
      await api.post("/invites/accept", {
        token,
        full_name: fullName,
        email,
        nmls_number: nmls.trim() || null,
        password,
        confirm_password: confirm
      });
      // Auto-login after signup
      await login(email, password);
      setSuccess(true);
      setTimeout(() => navigate("/dashboard"), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const roleInfo = preview ? ROLE_INFO[preview.role] : null;

  // ── Error states ──────────────────────────────────────────
  if (previewErr) return (
    <div className="accept-page">
      <div className="accept-bg"><div className="accept-grid" /><div className="accept-glow" /></div>
      <div className="accept-card error-card">
        <div className="error-icon">⚠️</div>
        <h2>Invalid Invite</h2>
        <p>{previewErr}</p>
        <p style={{color:"var(--text-muted)",fontSize:"0.85rem",marginTop:8}}>Contact your admin for a new invite link.</p>
      </div>
    </div>
  );

  if (preview?.is_used) return (
    <div className="accept-page">
      <div className="accept-bg"><div className="accept-grid" /><div className="accept-glow" /></div>
      <div className="accept-card error-card">
        <div className="error-icon">🔒</div>
        <h2>Already Used</h2>
        <p>This invite link has already been used to create an account.</p>
        <button className="btn-primary" style={{marginTop:20}} onClick={() => navigate("/login")}>Go to Login</button>
      </div>
    </div>
  );

  if (preview?.is_expired) return (
    <div className="accept-page">
      <div className="accept-bg"><div className="accept-grid" /><div className="accept-glow" /></div>
      <div className="accept-card error-card">
        <div className="error-icon">⏰</div>
        <h2>Invite Expired</h2>
        <p>This invite link expired after 72 hours. Contact your admin for a new one.</p>
      </div>
    </div>
  );

  if (success) return (
    <div className="accept-page">
      <div className="accept-bg"><div className="accept-grid" /><div className="accept-glow" /></div>
      <div className="accept-card success-final">
        <div className="success-anim">✓</div>
        <h2>Welcome to CommPro!</h2>
        <p>Your account has been created. Taking you to your dashboard...</p>
      </div>
    </div>
  );

  if (!preview) return (
    <div className="accept-page">
      <div className="accept-bg"><div className="accept-grid" /><div className="accept-glow" /></div>
      <div className="accept-card" style={{textAlign:"center",color:"var(--text-muted)"}}>Loading invite...</div>
    </div>
  );

  return (
    <div className="accept-page">
      <div className="accept-bg"><div className="accept-grid" /><div className="accept-glow" /></div>

      <div className="accept-card">
        {/* Logo */}
        <div className="accept-logo"><span className="accept-logo-icon">⬡</span> CommPro</div>

        {/* Step indicator */}
        <div className="accept-stepper">
          {steps.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`astep ${step === i ? "active" : ""} ${step > i ? "done" : ""}`}>
                <div className="astep-dot">{step > i ? "✓" : i + 1}</div>
                <div className="astep-label">{s}</div>
              </div>
              {i < steps.length - 1 && <div className={`astep-line ${step > i ? "done" : ""}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* ── Step 0: Welcome ── */}
        {step === 0 && (
          <div className="step-content">
            <div className={`role-welcome-badge role-${roleInfo?.color}`}>
              {roleInfo?.icon} You're joining as a {roleInfo?.label}
            </div>
            <h2>You've been invited to CommPro</h2>
            <p className="step-subtitle">
              <strong>{preview.inviter_name}</strong> has invited you to join their mortgage commission platform.
              This will take about 2 minutes.
            </p>
            <div className="welcome-perks">
              {preview.role === "loan_officer" && <>
                <div className="perk">📊 View your loan and commission history</div>
                <div className="perk">🔔 Track commission status in real time</div>
                <div className="perk">📝 Submit exception requests</div>
              </>}
              {preview.role === "manager" && <>
                <div className="perk">👥 View your full team's performance</div>
                <div className="perk">✅ Approve or reject exception requests</div>
                <div className="perk">📈 Access team commission dashboards</div>
              </>}
              {preview.role === "admin" && <>
                <div className="perk">⚙️ Full platform access and control</div>
                <div className="perk">📤 Upload and manage commission data</div>
                <div className="perk">👤 Manage all users and teams</div>
              </>}
              {preview.role === "executive" && <>
                <div className="perk">📊 Organization-wide commission overview</div>
                <div className="perk">📈 High-level dashboards and reporting</div>
                <div className="perk">🔒 Read-only — no accidental changes</div>
              </>}
            </div>
            <button className="btn-primary next-btn" onClick={() => setStep(1)}>
              Get Started →
            </button>
          </div>
        )}

        {/* ── Step 1: Your Info ── */}
        {step === 1 && (
          <div className="step-content">
            <h2>Tell us about yourself</h2>
            <p className="step-subtitle">This is how you'll appear to others in CommPro.</p>
            <div className="accept-form">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                  placeholder="Jane Smith"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Work Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  readOnly={!!preview.email}
                  style={preview.email ? {opacity:0.7} : {}}
                />
                {preview.email && <span className="field-hint">Email was pre-set by your admin</span>}
              </div>
              {preview.role === "loan_officer" && (
                <div className="form-group">
                  <label>NMLS Number</label>
                  <input
                    type="text"
                    value={nmls}
                    onChange={e => setNmls(e.target.value)}
                    placeholder="e.g. 123456"
                  />
                </div>
              )}
              <button
                className="btn-primary next-btn"
                onClick={() => {
                  if (!fullName || !email) { setError("Please fill in all fields"); return; }
                  if (preview.role === "loan_officer" && !nmls.trim()) { setError("NMLS number is required"); return; }
                  setError(""); setStep(2);
                }}
              >
                Continue →
              </button>
              {error && <div className="form-error">{error}</div>}
            </div>
          </div>
        )}

        {/* ── Step 2: Password ── */}
        {step === 2 && (
          <div className="step-content">
            <h2>Create your password</h2>
            <p className="step-subtitle">Use at least 8 characters. A strong password keeps your data safe.</p>
            <div className="accept-form">
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={e => { setPassword(e.target.value); checkStrength(e.target.value); }}
                  placeholder="••••••••"
                  autoFocus
                />
                {password && (
                  <div className="strength-bar">
                    <div className="strength-track">
                      <div className="strength-fill" style={{ width: `${pwStrength * 20}%`, background: strengthColor }} />
                    </div>
                    <span className="strength-label" style={{ color: strengthColor }}>{strengthLabel}</span>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Confirm Password</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  placeholder="••••••••"
                />
                {confirm && password !== confirm && (
                  <span className="field-hint" style={{color:"var(--danger)"}}>Passwords do not match</span>
                )}
              </div>
              {error && <div className="form-error">{error}</div>}
              <button
                className="btn-primary next-btn"
                onClick={handleSubmit}
                disabled={submitting || password !== confirm || password.length < 8}
              >
                {submitting ? "Creating account..." : "Create My Account →"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
